/**
 * Reading one account's listing data off the listing's placements (`platformAccounts`).
 *
 * A placement stores an account's edit in TWO halves: the marketplace item-specifics inside
 * `platformAspects`, and the fields promoted to their own columns (title, price, description, …).
 * The form binds ONE object per account, so these helpers put the two halves back together.
 */

// The per-account fields a placement keeps in dedicated columns rather than inside the
// platformAspects JSON. "Use as Template" deliberately drops them, so a copied listing falls back
// to the main listing values instead of inheriting another account's overrides.
export const LPA_PROMOTED_OVERRIDE_FIELDS = [
  'title', 'description', 'plainDescription',
  'price', 'currency', 'lengthUnits', 'weightUnits',
];

/**
 * The placement a platform's data is read from: the live account if there is one, else the row left
 * behind — a sold-out post or a prepared draft still holds that account's data.
 *
 * @param {Array|null} placements - The listing's placements (all platforms).
 * @param {string} platform - e.g. 'depop'.
 * @returns {Object|null} The placement to read, or null when the listing has none there.
 */
export function routablePlacement (placements, platform) {
  const onPlatform = (placements || []).filter(p => p && p.platform === platform);
  return onPlatform.find(p => p.status === 'active') || onPlatform[0] || null;
}

/**
 * One account's placement on the listing, whatever its status — an inactive placement can still
 * point at an item that is on the marketplace.
 *
 * @param {Array|null} placements - The listing's placements (all platforms).
 * @param {number|string} integrationId - The account.
 * @returns {Object|null} The placement, or null when the account has none on this listing.
 */
export function placementForAccount (placements, integrationId) {
  return (placements || []).find(p => String(p?.integrationId) === String(integrationId)) || null;
}

/**
 * An account's own override columns — its title, description, price and units — that hold a value.
 * An account without one of them follows the main listing field.
 *
 * @param {Object|null} placement - A placement row, or a remembered account.
 * @returns {Object} The override values, keyed by field.
 */
export function placementOverrides (placement) {
  const values = {};
  LPA_PROMOTED_OVERRIDE_FIELDS.forEach((field) => {
    if (placement?.[field] != null) values[field] = placement[field];
  });
  return values;
}

/**
 * One account's complete form values: its stored marketplace aspects plus its own override columns.
 *
 * Only a column holding a value counts — an account with no override keeps falling back to the main
 * listing field — and a column always wins over a key of the same name inside the aspects, because
 * the column is where that field now lives.
 *
 * @param {Object|null} placement - A placement row.
 * @returns {Object} The values the form shows for that account.
 */
export function placementFormValues (placement) {
  return { ...(placement?.platformAspects || {}), ...placementOverrides(placement) };
}

/**
 * A marketplace's aspects as the extension is sent them: the answers the listing holds for that
 * marketplace, with the account's own values (placementFormValues) laid over them — its title, price
 * and description among them. Undefined when the listing holds neither.
 *
 * @param {Object} listing - A listing as the server serves it, with its placements.
 * @param {string} platform - e.g. 'grailed'.
 * @returns {Object|undefined}
 */
export function aspectsAsSent (listing, platform) {
  const merged = {
    ...(listing?.[`${platform}Aspects`] || {}),
    ...placementFormValues(routablePlacement(listing?.platformAccounts, platform)),
  };
  return Object.keys(merged).length > 0 ? merged : listing?.[`${platform}Aspects`];
}
